import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback
} from "react";
import { useLocation, useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import ContentGutter from "../ContentGutter";
import SiteWrapper from "../SiteWrapper";
import Name from "./Name";
import Textfield from "./Textfield";
import Textarea from "./Textarea";
import IntegerInput from "./IntegerInput";
import Height from "./Height";
import Weight from "./Weight";
import GdprConsent from "./GdprConsent";
import BloodPressure from "./BloodPressure";
import MeasurementSystemPicker from "./MeasurementSystemPicker";
import BirthDate from "./BirthDate";
import {
  NAME,
  FIRST_NAME,
  LAST_NAME,
  GENDER,
  HEIGHT,
  WEIGHT,
  MEASUREMENT_SYSTEM,
  WEIGHT_UNIT,
  HEIGHT_UNIT,
  DESIRED_WEIGHT,
  WEIGHT_PRIOR,
  WEIGHT_RESULT,
  GDPR,
  MEDICAL,
  STATE,
  PANCREATITIS_CAUSE,
  PANCREATITIS_CAUSE_UNKNOWN,
  PANCREATITIS_YEAR,
  GALLBLADDER_YEAR,
  GALLBLADDER_EXPERIENCE,
  ALLERGEN_MEDICATION,
  BLOOD_PRESSURE,
  BLOOD_PRESSURE_UNKNOWN,
  BLOOD_PRESSURE_SYS,
  BLOOD_PRESSURE_DIA,
  HEART_RATE,
  DOB,
  EMAIL,
  LOCAL_STORAGE_EMAIL,
  ACCOUNT_CREATION,
  PASSWORD,
  PHONE_NUMBER,
  MAILING_ADDRESS,
  ADDRESS_LINE_1,
  CITY,
  ZIPCODE,
  SINGLE_CHOICE,
  SESSION_STORAGE_FOOD_NOISE
} from "./constants";
import {
  WITH_GDPR,
  LESS_THAN_25LBS,
  MORE_THAN_25LBS,
  MORE_THAN_50LBS,
  IMG_WIDTH_100_PERC,
  NO_PROGRESS_BAR,
  ACCOUNT_CREATION as ACCOUNT_CREATION_TAG,
  FOOTER_TEXT_UNDER_CTA,
  WEIGHT_HAS_EXCEEDED_LIMIT
} from "../../config/tags";
import { getMarketByLocale } from "../../utils/locales";
import toPrecision from "../../utils/toPrecision";
import convertWeight from "../../utils/weightUnits";
import convertHeight from "../../utils/heightUnits";
import useAppContext from "../AppContext";
import useUrlSlugContext from "../UrlSlugContext";
import {
  usesBothSystems,
  getAlternateSystem
} from "../../utils/measurementSystem";
import t, { AsHtml } from "../../utils/translate";
import replaceImages from "../../utils/replaceImages";
import { pageView, useNextButtonDaLabel } from "../../utils/analytics";
import { mergeAppState, isAtFirstQuestion } from "../../utils/branching";
import { getBMI, getWeightFromBmi } from "../../utils/bmi";
import ConditionalWrapper from "../ConditionalWrapper";
import ProgressBar from "../ProgressBar";
import SequenceProgressBar from "../SequenceProgressBar";
import cx from "classnames";
import StickyFooter from "../StickyFooter";
import SequenceFooter from "../SequenceFooter";
import Modal from "./Modal";
import { ChoiceButton } from "@ww-digital/web-foundations-react/modules";
import { CheckboxGroup, DateField } from "@ww-digital/prism";
import {
  states,
  stateText,
  statesv2,
  statesNoTerritories
} from "../../utils/stateData";
import { sendDataToGpcEli, sendDataToGpcFoodNoise } from "../../utils/gpc";
import SelectBox from "../SelectBox";
import AccountCreate from "./AccountCreate";
import Address from "./Address";
import Phone from "./Phone";
import useDelay from "../../utils/useDelay";
import useAsyncPagesHandler from "../../utils/useAsyncPagesHandler";
import useAwaitStateChange from "../../utils/useAwaitStateChange";
import replaceName from "../../utils/replaceName";
import routes from "../../routes";
import { formatPhoneNumber } from "utils/formatPhoneNumber";
import { processBmi } from "./utils/processBmi";
import { getWeightLossResults } from "./utils/getWeightLossResults";
import { logger } from "utils/logger";
import { getClinicTabData } from "utils/getClinicTabData";
import { checkEmail } from "utils/account";

const isNextDisabled = (profile, fields, withGdprConsent, gdprConsentValue) => {
  if (withGdprConsent && !gdprConsentValue) {
    return true;
  }

  let valid = true;

  fields.forEach(field => {
    if (field === NAME) {
      valid = valid && !!profile[FIRST_NAME] && !!profile[LAST_NAME];
    }

    if (field === FIRST_NAME) {
      valid = valid && !!profile[FIRST_NAME];
    }

    if (field === GENDER) {
      valid = valid && !!profile[field];
    }

    if (field === ACCOUNT_CREATION) {
      valid =
        valid &&
        !!profile[FIRST_NAME] &&
        !!profile[LAST_NAME] &&
        !!profile[MEDICAL][EMAIL] &&
        !!profile[PASSWORD];
    }

    if (field === MAILING_ADDRESS) {
      valid =
        valid &&
        !!profile[MAILING_ADDRESS][ADDRESS_LINE_1] &&
        !!profile[MAILING_ADDRESS][CITY] &&
        !!profile[MAILING_ADDRESS][STATE] &&
        !!profile[MAILING_ADDRESS][ZIPCODE];
    }

    if (field === PHONE_NUMBER) {
      valid = valid && formatPhoneNumber(profile[PHONE_NUMBER])?.valid;
    }

    if ([HEIGHT, WEIGHT, DESIRED_WEIGHT, WEIGHT_PRIOR].includes(field)) {
      valid = valid && !!profile[profile[MEASUREMENT_SYSTEM]][field];
    }

    // Required fields.
    if (
      [
        EMAIL,
        ALLERGEN_MEDICATION,
        GALLBLADDER_EXPERIENCE,
        HEART_RATE,
        PANCREATITIS_YEAR,
        GALLBLADDER_YEAR,
        DOB
      ].includes(field)
    ) {
      valid = valid && !!profile[MEDICAL][field];
    }
    if (field === STATE) {
      valid =
        (valid && !!profile[MEDICAL][STATE]) ||
        profile[MAILING_ADDRESS]?.[STATE];
    }
    // Field with "I do not know" checkbox.
    if (field === PANCREATITIS_CAUSE) {
      const allowEmpty = profile[MEDICAL][PANCREATITIS_CAUSE_UNKNOWN] === true;
      const pancreatitisCause = profile[MEDICAL][PANCREATITIS_CAUSE] ?? "";

      valid = valid && (allowEmpty || pancreatitisCause.length !== 0);
    }

    // Field with "I do not know" checkbox.
    if (field === BLOOD_PRESSURE_UNKNOWN) {
      valid =
        valid &&
        ((!!profile[MEDICAL] &&
          !!profile[MEDICAL][BLOOD_PRESSURE_SYS] &&
          !!profile[MEDICAL][BLOOD_PRESSURE_DIA]) ||
          !!profile[MEDICAL][BLOOD_PRESSURE_UNKNOWN]);
    }
  });

  return !valid;
};

const parseAnswerFields = (answers, questionTags) => {
  const allFields = [
    NAME,
    FIRST_NAME,
    GENDER,
    HEIGHT,
    WEIGHT,
    DESIRED_WEIGHT,
    WEIGHT_PRIOR,
    WEIGHT_RESULT,
    GDPR,
    STATE,
    PANCREATITIS_CAUSE,
    PANCREATITIS_CAUSE_UNKNOWN,
    PANCREATITIS_YEAR,
    GALLBLADDER_YEAR,
    GALLBLADDER_EXPERIENCE,
    ALLERGEN_MEDICATION,
    BLOOD_PRESSURE,
    BLOOD_PRESSURE_UNKNOWN,
    HEART_RATE,
    DOB,
    EMAIL,
    ACCOUNT_CREATION,
    PHONE_NUMBER,
    MAILING_ADDRESS
  ];

  if (questionTags.includes(ACCOUNT_CREATION_TAG)) {
    return [ACCOUNT_CREATION];
  }

  const profileDefault = [
    NAME,
    GENDER,
    HEIGHT,
    WEIGHT,
    ...(questionTags.includes(WITH_GDPR) ? [GDPR] : [])
  ];

  if (
    !answers ||
    !answers.presentationMetadata ||
    !answers.presentationMetadata.length
  ) {
    return profileDefault;
  }

  const fields = [];
  answers.presentationMetadata.forEach(answer => {
    if (answer.text && allFields.includes(answer.text)) {
      fields.push(answer.text);
    }
  });

  return fields.length ? fields : profileDefault;
};

const isWithGdpr = fields => fields.some(field => field === GDPR);

const isValidRegex = (value, regex) => {
  const pattern = new RegExp(regex);
  return pattern.test(value);
};

export default function Profile({ onNext, onBack, question, nextQuestion }) {
  const location = useLocation();
  const { questionPath } = useParams();
  const rules = window.ww.validations;
  const scroll = useRef(styles.nonStickyMainWrapper);
  const nextFunctionDelayed = useRef(false);
  const market = getMarketByLocale(question.locale);
  const fields = parseAnswerFields(question.answers, question.tags);
  const canToggleSystems = useMemo(() => usesBothSystems(market), [market]);
  const isSingle = question.answers.presentationType === SINGLE_CHOICE;

  const hasSystemField = fields.some(r =>
    [HEIGHT, WEIGHT, DESIRED_WEIGHT].includes(r)
  );
  const { baseUrl } = useUrlSlugContext();
  const {
    assessment,
    newQuiz,
    profile,
    setProfile,
    authData,
    setAuthData,
    autoAdvance
  } = useAppContext();
  const { loggedInAs } = authData || {};

  const { delay } = useDelay();

  const atFirstQuestion = isAtFirstQuestion(
    questionPath,
    assessment?.questions
  );

  const daLabelNextButton = useNextButtonDaLabel(
    question,
    null,
    nextQuestion,
    profile
  );
  const [isDisabled, setDisabled] = useState(true);
  const updateProfile = (key, value, firstLevelKey = null) => {
    let modifiedProfile = null;

    if (!firstLevelKey) {
      modifiedProfile = { ...profile, [key]: value };
    } else {
      modifiedProfile = {
        ...profile,
        [firstLevelKey]: {
          ...profile[firstLevelKey],
          [key]: value
        }
      };
    }

    setProfile(modifiedProfile);
    mergeAppState({ profile: modifiedProfile }, location, baseUrl);
    setDisabled(isNextDisabled(modifiedProfile, fields, withGdpr, gdprConsent));
  };

  const { handleAsyncPages, createAccountError, callIsPending } =
    useAsyncPagesHandler({
      question,
      profile,
      updateProfile,
      authData,
      setAuthData
    });

  const [gdprConsent, setGdprConsent] = useState(false);
  const withGdpr = isWithGdpr(fields);

  const updateStat = ({ value, STAT, STAT_UNIT, convert, weightAt27Bmi }) => {
    const modifiedProfile = { ...profile };
    const CUR_SYSTEM = profile[MEASUREMENT_SYSTEM];
    const measurements = { ...profile[CUR_SYSTEM] };
    measurements[STAT] = value;
    modifiedProfile[CUR_SYSTEM] = measurements;

    // Ensure we have metric stats available for BMI calculation.
    const ALT_SYSTEM = getAlternateSystem(CUR_SYSTEM);
    const fromUnit = profile[CUR_SYSTEM][STAT_UNIT];
    const toUnit = profile[ALT_SYSTEM][STAT_UNIT];
    const convertedValue = toPrecision(convert(value, fromUnit, toUnit));
    const otherSystemMeasurements = { ...profile[ALT_SYSTEM] };
    otherSystemMeasurements[STAT] = convertedValue;
    modifiedProfile[ALT_SYSTEM] = otherSystemMeasurements;

    if (modifiedProfile?.metric.height && modifiedProfile?.metric.weight) {
      modifiedProfile.bmi = getBMI(
        modifiedProfile.metric.height,
        modifiedProfile.metric.weight,
        modifiedProfile.metric.heightUnit
      );
      modifiedProfile.goalBMI = getBMI(
        modifiedProfile.metric.height,
        modifiedProfile.metric.desiredWeight,
        modifiedProfile.metric.heightUnit
      );
    }

    // Logging for an error where some users are missing weight.
    if (
      newQuiz.isSequence &&
      STAT === WEIGHT &&
      modifiedProfile?.metric?.weight === null
    ) {
      logger("Missing weight", {
        level: "error",
        customAttributes: { value }
      });
    }

    if (STAT === WEIGHT_PRIOR) {
      const weightHasExceededLimit = value > weightAt27Bmi;
      modifiedProfile[MEDICAL][WEIGHT_HAS_EXCEEDED_LIMIT] =
        weightHasExceededLimit;
    }

    setDisabled(isNextDisabled(modifiedProfile, fields, withGdpr, gdprConsent));
    setProfile(modifiedProfile);
    mergeAppState({ profile: modifiedProfile }, location, baseUrl);
  };

  const weightLossResults = () => getWeightLossResults(profile, assessment);

  const { isFromClinicTab } = getClinicTabData();

  useEffect(() => {
    if (newQuiz.isFoodNoise) {
      pageView(question.urlPath, "fnqz");
    } else {
      pageView(`profile_capture:${question.urlPath}`);
    }
  }, [question.urlPath]);

  useEffect(() => {
    const newFields = parseAnswerFields(question.answers, question.tags);
    setDisabled(isNextDisabled(profile, newFields, withGdpr, gdprConsent));
  }, [profile, question]);

  const quizHasGender =
    newQuiz.isSequence || newQuiz.isUni || newQuiz.isClinical;
  useEffect(() => {
    if (quizHasGender && fields.includes(GENDER)) {
      updateProfile(GENDER, null);
    }
  }, [question.urlPath]);

  const onResultsPage =
    question.tags.includes(LESS_THAN_25LBS) ||
    question.tags.includes(MORE_THAN_25LBS) ||
    question.tags.includes(MORE_THAN_50LBS);

  const imageMargin = "32px auto";
  const imageWidth = !question.tags.includes(IMG_WIDTH_100_PERC)
    ? "auto"
    : undefined;
  const imageDisplay = "block";
  const headerText1 = replaceName(
    replaceImages(
      question.headerText1,
      question.images,
      imageMargin,
      imageWidth,
      imageDisplay
    ),
    profile[FIRST_NAME]
  );
  // Show headerText2 and footerText1 if we're showing the weight loss results page.
  const headerText2 = onResultsPage
    ? replaceImages(
        weightLossResults().headerText2,
        question.images,
        imageMargin,
        imageWidth,
        imageDisplay
      )
    : replaceImages(
        question.headerText2,
        question.images,
        imageMargin,
        imageWidth,
        imageDisplay
      );
  const footerText1 = onResultsPage
    ? replaceImages(
        weightLossResults().footerText1,
        question.images,
        imageMargin,
        imageWidth,
        imageDisplay
      )
    : replaceImages(
        question.footerText1,
        question.images,
        imageMargin,
        imageWidth,
        imageDisplay
      );

  const handleFormSubmit = e => {
    e.preventDefault();
  };

  // BMI variables.
  const { bmiMessage, headerMessage, showGoalMessage, showStartingMessage } =
    processBmi(profile, question);

  const noProgressBar = question.tags.includes(NO_PROGRESS_BAR);

  const onBackQuestion = () => {
    onBack();
    document.getElementsByClassName(scroll.current)[0].scrollTop = 0;

    if (profile[PASSWORD]) {
      updateProfile(PASSWORD, "");
    }
  };

  const backButtonProps = {
    onBack: onBackQuestion,
    "da-category": newQuiz.isFoodNoise ? "food_noise_questionnaire" : "quiz",
    "da-action": "back_button",
    "data-testid": "back-button"
  };

  const progressBar = newQuiz.isSequence ? (
    noProgressBar ? null : (
      <SequenceProgressBar
        question={question}
        backButtonProps={backButtonProps}
      />
    )
  ) : (
    <ProgressBar
      question={question}
      backButtonProps={backButtonProps}
      withProgressBar={!noProgressBar}
    />
  );

  const [toggleModal, setToggleModal] = useState(false);

  const handleKeypadNextPress = useCallback(event => {
    if (
      fields.includes(GALLBLADDER_EXPERIENCE) ||
      fields.includes(PANCREATITIS_CAUSE)
    ) {
      return;
    }

    const { keyCode } = event;

    if (keyCode === 13) {
      if (document.querySelector('[class*="stickyNext"]')) {
        document.querySelector('[class*="stickyNext"]').click();
      } else if (document.querySelector('[class*="roundButton"]')) {
        document.querySelector('[class*="roundButton"]').click();
      } else if (document.querySelector('[class*="nextButton"]')) {
        document.querySelector('[class*="nextButton"]').click();
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeypadNextPress);
    return () => {
      window.removeEventListener("keydown", handleKeypadNextPress);
    };
  }, [handleKeypadNextPress]);

  const isAccountCreationScreen = location.pathname === routes.createAccount;
  const bypassValidation = isAccountCreationScreen && authData?.loggedInAs;

  const onNextFunction =
    showGoalMessage || showStartingMessage
      ? () => setToggleModal(!toggleModal)
      : async () => {
          if (autoAdvance && nextFunctionDelayed.current) return;
          if (newQuiz.isSequence || newQuiz.isUni3 || newQuiz.isUni4) {
            const proceed = await handleAsyncPages();
            if (!proceed) return;
          }
          if (autoAdvance && isSingle) {
            const CLINICAL_AUTO_ADVANCE_DELAY = 200;
            const DEFAULT_AUTO_ADVANCE_DELAY = 700;

            nextFunctionDelayed.current = true;
            if (newQuiz.isClinical) {
              await delay(CLINICAL_AUTO_ADVANCE_DELAY);
            } else {
              await delay(DEFAULT_AUTO_ADVANCE_DELAY);
            }
            nextFunctionDelayed.current = false;
          }
          if (newQuiz.isFoodNoise && fields.includes(EMAIL)) {
            const gpcData = await sendDataToGpcFoodNoise(
              profile[MEDICAL][EMAIL]
            );

            const checkEmailData = await checkEmail({
              email: profile[MEDICAL][EMAIL]
            });

            if (!gpcData || !checkEmailData) {
              return;
            }

            window.sessionStorage.setItem(
              SESSION_STORAGE_FOOD_NOISE,
              JSON.stringify({
                uuid: gpcData.uuid,
                isActive: checkEmailData.isActive || false,
                emailStatus: checkEmailData.emailStatus || ""
              })
            );
          }
          onNext(profile);
          document.getElementsByClassName(scroll.current)[0].scrollTop = 0;
          if (newQuiz.eli && fields.includes(EMAIL)) {
            sendDataToGpcEli();
          }
        };

  const { awaitStateChange } = useAwaitStateChange(
    () => updateProfile,
    [profile.gender],
    onNextFunction
  );

  const nextButtonProps = {
    onNext: bypassValidation ? onNext(profile) : onNextFunction,
    id: question._id,
    isDisabled: isDisabled,
    "da-category": "prepaywall_assessment",
    "da-action": "next",
    "da-label": daLabelNextButton,
    pending: callIsPending,
    className: newQuiz.isUni3 || newQuiz.isUni4 ? styles.nextButton : ""
  };

  const showFooterTextUnderCta = question.tags.includes(FOOTER_TEXT_UNDER_CTA);
  const weightAt27Bmi = getWeightFromBmi(profile.metric?.height, 27);

  return (
    <div
      className={cx(styles.nonStickyMainWrapper, {
        [styles.nonStickyMainWrapperIphone]: newQuiz.isIphone,
        [styles.nonStickyMainWrapperAndroid]: newQuiz.isAndroid,
        [styles.isClinical]: newQuiz.isClinical,
        [styles.hasNextButton]: !isDisabled,
        [styles.hasLogin]: authData?.loggedInAs,
        [styles.isSingle]: isSingle,
        [styles.isFromClinicTab]: isFromClinicTab
      })}
    >
      <div className={styles.screenHeader}>{progressBar}</div>

      <ContentGutter>
        <SiteWrapper size="small" contentWrapper>
          <AsHtml
            className={cx(styles.header, {
              [styles.headerNoDescription]: newQuiz.isSequence && !headerText2
            })}
            value={headerText1}
            tagType="div"
            interpolations={{ name: profile.firstName }}
          />
          {headerText2 && (
            <AsHtml
              className={styles.description}
              value={headerText2}
              tagType="div"
              interpolations={{
                name: profile.firstName,
                weightAt27Bmi
              }}
            />
          )}

          <form className={styles.profile} onSubmit={handleFormSubmit}>
            {fields.map((field, index) => {
              if (field === FIRST_NAME) {
                return (
                  <Name
                    key={`first-name-${index}`}
                    value={profile}
                    onChange={updateProfile}
                    noPlaceholder={false}
                    rules={{
                      firstName: rules.firstName
                    }}
                  />
                );
              }

              if (field === EMAIL) {
                return (
                  <Textfield
                    id={EMAIL}
                    name={EMAIL}
                    key={`email-${index}-${!!loggedInAs}`}
                    placeholder={t("EMAIL_PLACEHOLDER")}
                    value={profile[MEDICAL][EMAIL]}
                    onChange={(key, value) => {
                      if (!newQuiz.isFoodNoise) {
                        window.localStorage.setItem(LOCAL_STORAGE_EMAIL, value);
                      }
                      updateProfile(key, value, MEDICAL);
                    }}
                    rules={{
                      errorMessages: {
                        isRequired: t("FIELD_ERROR_REQUIRED"),
                        isMoreThanMax: t("EMAIL_ERROR_TOO_LONG"),
                        isInvalidPattern: t("EMAIL_ERROR_INVALID")
                      },
                      validators: {
                        isRequired: value => String(value).length === 0,
                        isMoreThanMax: value =>
                          rules.email.max <= String(value).length,
                        isInvalidPattern: value =>
                          !isValidRegex(value, rules.email.regex)
                      }
                    }}
                    styles={styles}
                  />
                );
              }

              if (field === GALLBLADDER_EXPERIENCE) {
                return (
                  <Textarea
                    id={GALLBLADDER_EXPERIENCE}
                    name={GALLBLADDER_EXPERIENCE}
                    key={`gallbladder-experience-${index}`}
                    onChange={(key, value) =>
                      updateProfile(key, value, MEDICAL)
                    }
                    value={profile[MEDICAL][GALLBLADDER_EXPERIENCE]}
                    rules={{
                      errorMessages: {
                        isRequired: t("FIELD_ERROR_REQUIRED")
                      },
                      validators: {
                        isRequired: value => String(value).length === 0
                      }
                    }}
                    disabled={false}
                    placeholder={t("GALLBLADDER_EXPERIENCE_PLACEHOLDER")}
                    className={cx({
                      [styles.gallbladderExperienceText]:
                        newQuiz.isSequence && headerText2
                    })}
                  />
                );
              }

              if (field === PANCREATITIS_CAUSE) {
                return (
                  <Textarea
                    id={PANCREATITIS_CAUSE}
                    name={PANCREATITIS_CAUSE}
                    key={`pancreatitis-cause-${index}`}
                    onChange={(key, value) => {
                      updateProfile(key, value, MEDICAL);
                    }}
                    value={profile[MEDICAL][PANCREATITIS_CAUSE]}
                    rules={{
                      errorMessages: {
                        isRequired: t("FIELD_ERROR_REQUIRED")
                      },
                      validators: {
                        isRequired: value => String(value).length === 0
                      }
                    }}
                    disabled={profile[MEDICAL][PANCREATITIS_CAUSE_UNKNOWN]}
                    placeholder={t("PANCREATITIS_CAUSE_PLACEHOLDER")}
                  />
                );
              }

              if (field === ALLERGEN_MEDICATION) {
                return (
                  <Textfield
                    id={ALLERGEN_MEDICATION}
                    name={ALLERGEN_MEDICATION}
                    key={`allergen-medication-${index}`}
                    placeholder={t("ALLERGEN_MEDICATION_PLACEHOLDER")}
                    value={profile[MEDICAL][ALLERGEN_MEDICATION]}
                    onChange={(key, value) =>
                      updateProfile(key, value, MEDICAL)
                    }
                    rules={{
                      errorMessages: {
                        isRequired: t("FIELD_ERROR_REQUIRED")
                      },
                      validators: {
                        isRequired: value => String(value).length === 0
                      }
                    }}
                    styles={styles}
                  />
                );
              }

              if (field === HEART_RATE) {
                return (
                  <IntegerInput
                    key={`heart-rate-${index}`}
                    name={HEART_RATE}
                    label={t("HEART_RATE_LABEL")}
                    value={profile[MEDICAL][HEART_RATE]}
                    onChange={value =>
                      updateProfile(HEART_RATE, value, MEDICAL)
                    }
                    styles={styles}
                    min={40}
                    max={200}
                    errorMessage={t("HEART_RATE_INVALID")}
                    placeholder={
                      newQuiz.isSequence || newQuiz.isUni3 || newQuiz.isUni4
                        ? t("JS_HEART_RATE")
                        : ""
                    }
                  />
                );
              }

              if (field === GENDER) {
                const genderOptions = [
                  {
                    label: t("SEX_FEMALE"),
                    id: "F",
                    checked: profile[GENDER] === "F"
                  },
                  {
                    label: t("SEX_MALE"),
                    id: "M",
                    checked: profile[GENDER] === "M"
                  }
                ];
                return (
                  <ConditionalWrapper
                    condition={newQuiz.isSequence}
                    wrapper={children => (
                      <div className={styles.genderSection}>{children}</div>
                    )}
                    key={`gender-${index}`}
                  >
                    <CheckboxGroup
                      key={`gender-${index}`}
                      checkboxesData={genderOptions}
                      onCheckedChange={(checked, id) => {
                        updateProfile(GENDER, id);
                        // Wait for state change if gender actually changed.
                        if (
                          newQuiz.isUni5 ||
                          newQuiz.isSequence ||
                          newQuiz.isClinical ||
                          (newQuiz.isUni &&
                            !(newQuiz.isUni3 || newQuiz.isUni4) &&
                            id !== profile[GENDER])
                        ) {
                          awaitStateChange();
                        }
                        if (
                          newQuiz.isUni &&
                          !(newQuiz.isUni3 || newQuiz.isUni4) &&
                          id === profile[GENDER]
                        ) {
                          onNextFunction();
                        }

                        if (newQuiz.isFoodNoise) {
                          onNextFunction();
                        }
                      }}
                      checkboxLabelClassName={cx(styles.newQuizQuestionLabel, {
                        [styles.newQuizQuestionLabelIsSingle]:
                          newQuiz.isSequence && isSingle
                      })}
                      checkboxLabelSelectedClassName={cx(
                        styles.newQuizQuestionLabelSelected,
                        {
                          [styles.newQuizQuestionLabelSelectedIsSingle]:
                            autoAdvance && isSingle
                        }
                      )}
                      checkboxClassName={cx(styles.newQuizQuestionCheckbox, {
                        [styles.newQuizQuestionCheckboxAndroid]:
                          newQuiz.isAndroid
                      })}
                      checkboxSelectedClassName={cx({
                        [styles.checkboxSelected]:
                          autoAdvance && isSingle && !newQuiz.isClinical
                      })}
                      preset="pillLong"
                    />
                  </ConditionalWrapper>
                );
              }

              if (field === DOB) {
                let birthDate;

                // if we have a non-null date already
                if (profile[MEDICAL][DOB]) {
                  birthDate = profile[MEDICAL][DOB];
                }

                return (
                  <section key={`dob-${index}`}>
                    <BirthDate
                      date={birthDate}
                      onDateComplete={date => {
                        if (!(date instanceof Date) || isNaN(date)) {
                          return;
                        }

                        const [dob] = date.toISOString().split("T");
                        updateProfile("dob", dob, MEDICAL);
                      }}
                      onDateError={() => {
                        if (newQuiz.isClinical) {
                          updateProfile("dob", null, MEDICAL);
                        }
                      }}
                    />
                  </section>
                );
              }

              if (field === PANCREATITIS_YEAR) {
                let pancreatitisDate;

                // if we have a non-null date already
                if (profile[MEDICAL][PANCREATITIS_YEAR] !== null) {
                  pancreatitisDate = new Date();
                  pancreatitisDate.setUTCFullYear(
                    profile[MEDICAL][PANCREATITIS_YEAR]
                  );
                }

                return (
                  <section
                    key={`pancreatitis_year-${index}`}
                    className={styles.yearSelector}
                  >
                    <DateField
                      date={pancreatitisDate}
                      onDateComplete={date => {
                        if (!(date instanceof Date) || isNaN(date)) {
                          return;
                        }

                        const year = date.getFullYear();
                        updateProfile("pancreatitisYear", year, MEDICAL);
                      }}
                    >
                      <DateField.YearSelector
                        placeHolder="Select the year"
                        startYear={1920}
                        sortDescending
                      />
                    </DateField>
                  </section>
                );
              }

              if (field === GALLBLADDER_YEAR) {
                let gallbladderDate;

                // if we have a non-null date already
                if (profile[MEDICAL][GALLBLADDER_YEAR] !== null) {
                  gallbladderDate = new Date();
                  gallbladderDate.setUTCFullYear(
                    profile[MEDICAL][GALLBLADDER_YEAR]
                  );
                }

                return (
                  <section
                    key={`gallbladder-${index}`}
                    className={styles.yearSelector}
                  >
                    <DateField
                      date={gallbladderDate}
                      onDateComplete={date => {
                        if (!(date instanceof Date) || isNaN(date)) {
                          return;
                        }

                        const year = date.getFullYear();
                        updateProfile("gallbladderYear", year, MEDICAL);
                      }}
                    >
                      <DateField.YearSelector
                        placeHolder="Select the year"
                        startYear={1920}
                        sortDescending
                      />
                    </DateField>
                  </section>
                );
              }

              if (field === HEIGHT) {
                return (
                  <Height
                    key={`height-${index}`}
                    value={profile[profile[MEASUREMENT_SYSTEM]][HEIGHT]}
                    unit={profile[profile[MEASUREMENT_SYSTEM]][HEIGHT_UNIT]}
                    isShortUnit={!(newQuiz.isSequence || newQuiz.isClinical)}
                    market={market}
                    fieldsetWrapperClassName={styles.fieldsetWrapperNewQuiz}
                    labelClassName={styles.inputLabelNewQuiz}
                    capitalizeLabel={true}
                    onChange={value =>
                      updateStat({
                        value,
                        STAT: HEIGHT,
                        STAT_UNIT: HEIGHT_UNIT,
                        convert: convertHeight
                      })
                    }
                  />
                );
              }

              if (field === WEIGHT) {
                return (
                  <Weight
                    key={`weight-${index}`}
                    weightName="weightInput"
                    value={profile[profile[MEASUREMENT_SYSTEM]][WEIGHT]}
                    unit={profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_UNIT]}
                    isShortUnit={!newQuiz.isClinical}
                    market={market}
                    fieldsetWrapperClassName={styles.fieldsetWrapperNewQuiz}
                    labelClassName={styles.inputLabelNewQuiz}
                    capitalizeLabel={true}
                    onChange={value =>
                      updateStat({
                        value,
                        STAT: WEIGHT,
                        STAT_UNIT: WEIGHT_UNIT,
                        convert: convertWeight
                      })
                    }
                  />
                );
              }

              if (field === DESIRED_WEIGHT) {
                return (
                  <Weight
                    key={`desired-weight-${index}`}
                    weightName="desiredWeightInput"
                    value={profile[profile[MEASUREMENT_SYSTEM]][DESIRED_WEIGHT]}
                    unit={profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_UNIT]}
                    isShortUnit={!newQuiz.isClinical}
                    market={market}
                    fieldsetWrapperClassName={styles.fieldsetWrapperNewQuiz}
                    labelClassName={styles.inputLabelNewQuiz}
                    capitalizeLabel={true}
                    onChange={value =>
                      updateStat({
                        value,
                        STAT: DESIRED_WEIGHT,
                        STAT_UNIT: WEIGHT_UNIT,
                        convert: convertWeight
                      })
                    }
                  />
                );
              }

              if (field === WEIGHT_PRIOR) {
                return (
                  <Weight
                    key={`weight-prior-${index}`}
                    weightName="weightPriorInput"
                    value={profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_PRIOR]}
                    unit={profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_UNIT]}
                    isShortUnit={!newQuiz.isClinical}
                    market={market}
                    fieldsetWrapperClassName={styles.fieldsetWrapperNewQuiz}
                    labelClassName={styles.inputLabelNewQuiz}
                    capitalizeLabel={true}
                    onChange={value =>
                      updateStat({
                        value,
                        STAT: WEIGHT_PRIOR,
                        STAT_UNIT: WEIGHT_UNIT,
                        convert: convertWeight,
                        weightAt27Bmi
                      })
                    }
                  />
                );
              }

              if (field === WEIGHT_RESULT) {
                return (
                  <div
                    key="weight-result"
                    className={styles.weightLostResultWrapper}
                  >
                    <img
                      className={styles.weightLostResultImg}
                      src={
                        weightLossResults().images[newQuiz.isUni ? 0 : 1]?.url
                      }
                      alt={
                        weightLossResults().images[newQuiz.isUni ? 0 : 1]
                          ?.altTag
                      }
                    />
                  </div>
                );
              }

              if (field === GDPR) {
                return (
                  <React.Fragment
                    key={`measurement-picker-gdpr-group-${index}`}
                  >
                    {canToggleSystems && hasSystemField && (
                      <MeasurementSystemPicker
                        key={`measurement-picker-${index}`}
                        value={profile[MEASUREMENT_SYSTEM]}
                        onChange={value =>
                          updateProfile(MEASUREMENT_SYSTEM, value)
                        }
                      />
                    )}
                    <GdprConsent
                      key={`gdpr-${index}`}
                      value={gdprConsent}
                      onChange={value => {
                        setGdprConsent(value);
                        setDisabled(
                          isNextDisabled(profile, fields, withGdpr, value)
                        );
                      }}
                    />
                  </React.Fragment>
                );
              }

              if (
                field === BLOOD_PRESSURE_UNKNOWN ||
                field === PANCREATITIS_CAUSE_UNKNOWN
              ) {
                return (
                  <ChoiceButton
                    key={`dont-know-${index}`}
                    type="checkbox"
                    name="dont-know"
                    className={styles.checkbox}
                    value={true}
                    checked={profile[MEDICAL][field] || false}
                    title={t("DO_NOT_KNOW") || "I don't know"}
                    onChange={ev => {
                      updateProfile(field, ev.target.checked, MEDICAL);
                    }}
                  />
                );
              }

              if (field === BLOOD_PRESSURE) {
                return (
                  <BloodPressure
                    key={`blood-pressure-${index}`}
                    onChange={updateProfile}
                    profile={profile}
                    newQuiz={newQuiz}
                  />
                );
              }

              if (
                field === STATE &&
                (newQuiz.isSequence ||
                  newQuiz.isUni3 ||
                  newQuiz.isUni4 ||
                  newQuiz.isUni5 ||
                  newQuiz.isClinical)
              ) {
                return (
                  <SelectBox
                    onChange={selection => {
                      updateProfile(STATE, selection, MAILING_ADDRESS);
                    }}
                    selection={profile[MAILING_ADDRESS][STATE]}
                    key={`state-${index}`}
                    options={
                      newQuiz.isClinical || newQuiz.isUni5
                        ? statesNoTerritories
                        : statesv2
                    }
                    placeholder={t("STATE_SELECTBOX_LABEL_V2")}
                    groupLabel={t("STATE_SELECTBOX_LABEL_V2")}
                  />
                );
              }

              if (field === STATE) {
                return (
                  <SelectBox
                    onChange={selection => {
                      updateProfile(STATE, selection, MEDICAL);
                    }}
                    selection={profile[MEDICAL][STATE]}
                    key={`state-${index}`}
                    options={newQuiz.eli ? statesNoTerritories : states}
                    placeholder={stateText}
                    groupLabel={stateText}
                  />
                );
              }

              if (field === ACCOUNT_CREATION) {
                return (
                  <AccountCreate
                    key={`account-create-${index}`}
                    onChange={updateProfile}
                    createAccountError={createAccountError}
                  />
                );
              }

              if (field === PHONE_NUMBER) {
                return (
                  <div key={`phone-number-${index}`}>
                    <Phone onChange={updateProfile} />
                  </div>
                );
              }

              if (field === MAILING_ADDRESS) {
                return (
                  <div key={`mailing-address-${index}`}>
                    <Address
                      onChange={(key, value) => {
                        updateProfile(key, value, MAILING_ADDRESS);
                      }}
                    />
                  </div>
                );
              }

              return null;
            })}
          </form>
          {footerText1 &&
            !showFooterTextUnderCta &&
            !(newQuiz.isSequence && atFirstQuestion) && (
              <div
                className={cx(styles.footer, {
                  [styles.footerNewQuizWeightResult]:
                    !newQuiz.isSequence && onResultsPage
                })}
              >
                <AsHtml
                  value={footerText1}
                  interpolations={{ name: profile.firstName }}
                />
              </div>
            )}
        </SiteWrapper>
      </ContentGutter>
      {(!!showGoalMessage || !!showStartingMessage) && (
        <Modal
          isOpen={toggleModal}
          onOpenChange={() => setToggleModal(!toggleModal)}
          title={headerMessage}
          description={bmiMessage}
          close={t("CLOSE_MODAL")}
        />
      )}
      {newQuiz.isSequence ||
      ((newQuiz.isUni3 || newQuiz.isUni4) && isAccountCreationScreen) ? (
        <SequenceFooter
          nextButtonProps={nextButtonProps}
          hideButton={isSingle}
          message={atFirstQuestion && footerText1 ? footerText1 : null}
        />
      ) : (
        !((autoAdvance && isSingle) || (newQuiz.isClinical && isDisabled)) && (
          <StickyFooter
            nextButtonProps={nextButtonProps}
            message={{ label: showFooterTextUnderCta ? footerText1 : null }}
          />
        )
      )}
    </div>
  );
}
